import {
  BuildingIcon,
  CalendarIcon,
  CarIcon,
  CheckCircle2Icon,
  ClipboardListIcon,
  ClockIcon,
  ExternalLinkIcon,
  HourglassIcon,
  MapPinIcon,
  ReceiptTextIcon,
  RefreshCwIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  StoreIcon,
  UserIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { DevicesTab } from "@/components/orders/devices-tab"
import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import { TransactionsTable } from "@/components/transactions"
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { usePermission } from "@/contexts/permission"
import type {
  BillingCycleStatusEnum,
  RecurringUnitEnum,
  StatusTrackingEnum,
  SubscriptionStatusEnum,
  SubscriptionTypeEnum,
} from "@/enums"
import {
  billingCycleStatusLabels,
  GenericStatusEnum,
  PaymentMethodNameEnum,
  PermissionsRolesEnum,
  recurringUnitLabels,
  SalesChannelEnum,
  statusToGenericStatus,
  StatusTrackingEnum as StatusEnum,
  subscriptionStatusLabels,
  subscriptionTypeLabels,
} from "@/enums"
import { formatDocument } from "@/helpers/date-helper"
import {
  formatMoney,
  formatPhoneNumber,
} from "@/helpers/format"
import {
  useOrderById,
  useOrdersSearchParams,
} from "@/hooks"
import { toast } from "@/hooks/use-toast"
import { useHasOrderInsuranceExpired } from "@/hooks/webhooks"
import { cn } from "@/lib/utils"
import { useExpireOrderMutation } from "@/services/orders/expire-order"
import { useReprocessPaymentEventMutation } from "@/services/orders/reprocess-payment-event"
import { DateHelper } from "@/utils"

import { getInsuranceTypeLabel } from "../subscriptions/SubscriptionsHelper"
import { Button, buttonVariants } from "../ui/button"
import { CancelDialog } from "./cancel-dialog"
import { ExportCancelLetter } from "./export-cancel-letter"
import { OrderDetailsSkeleton } from "./order-details-skeleton"
import { getPaymentMethodLabel } from "./payment-method-label"
import { ProductsTable } from "./products-table"
import { ReversalAction } from "./reversal-action"
import { OrdersSaleChannelBadge } from "./sales-channel-badge"
import { OrdersStatusBadge } from "./status-badge"
import { getAdditionalDetail } from "./status-details"
import { getStatusConfig } from "./status-tracking-config"

export const Details = () => {
  const { t } = useTranslation()
  const { params } = useOrdersSearchParams()
  const { hasPermission } = usePermission()
  const { data, isFetching, error } = useOrderById(
    params.orderId!
  )
  const {
    mutateAsync: expireOrder,
    isPending: isExpiring,
  } = useExpireOrderMutation(params.orderCode!)
  const {
    mutateAsync: reprocessPaymentEvent,
    isPending: isReprocessing,
  } = useReprocessPaymentEventMutation(params.orderCode!)

  // Controle de tab ativa via hash da URL
  const [activeTab, setActiveTab] = useState<string>(() => {
    const hash = window.location.hash.replace("#", "")
    return hash || "details"
  })

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    // Atualizar hash da URL
    window.history.pushState(null, "", `#${value}`)
  }

  const handleExpireOrder = async () => {
    if (!params.orderId) return

    await expireOrder(
      { orderId: params.orderId },
      {
        onSuccess: () => {
          toast({
            title: t("orders.expireSuccess"),
            variant: "success",
          })
        },
        onError: () => {
          toast({
            title: t("orders.expireError"),
            variant: "destructive",
          })
        },
      }
    )
  }

  const handleReprocessPaymentEvent = async () => {
    if (!data?.paymentId) return

    await reprocessPaymentEvent(
      { paymentId: data.paymentId },
      {
        onSuccess: () => {
          toast({
            title: t("orders.reprocessPaymentEventSuccess"),
            variant: "success",
          })
        },
        onError: () => {
          toast({
            title: t("orders.reprocessPaymentEventError"),
            variant: "destructive",
          })
        },
      }
    )
  }

  const hasData = Boolean(data)
  const customer = data?.customer
  const broker = data?.insurance?.broker
  const createdAt = data?.createdAt
    ? DateHelper.format(
        data.createdAt,
        "dd/MM/yyyy 'às' HH:mm"
      )
    : undefined
  const rawPhoneNumber =
    customer?.phone?.areaCode && customer?.phone?.number
      ? `${customer.phone.areaCode}${customer.phone.number}`
      : undefined
  const phoneNumber = rawPhoneNumber
    ? formatPhoneNumber(rawPhoneNumber)
    : undefined
  const hasProducts = Boolean(data?.items?.length)
  const hasBillingCycle = Boolean(data?.billingCycle)
  const hasSubscription = Boolean(
    data?.billingCycle?.subscription
  )
  const hasTransactions = Boolean(
    data?.transactions?.length
  )
  const hasCars = Boolean(
    data?.insurance?.policyInformation?.cars?.length
  )
  const hasPaymentMethods = (
    paymentMethod: PaymentMethodNameEnum
  ) => {
    return data?.transactions?.some(
      (transaction) => transaction.method === paymentMethod
    )
  }
  const isOpenFinancePayment = hasPaymentMethods(
    PaymentMethodNameEnum.OPEN_FINANCE
  )
  const getBillingCycleStatusLabel = (
    status?: BillingCycleStatusEnum
  ): string => {
    return status
      ? (billingCycleStatusLabels[status] ??
          t("common.unknown"))
      : t("common.undefined")
  }
  const currentStatus = data?.status
    ?.status as StatusTrackingEnum
  const genericStatus = currentStatus
    ? statusToGenericStatus[currentStatus]
    : undefined
  // O bucket genérico saiu da tela (rótulo e badge), mas segue mandando na
  // REGRA: é ele que decide banner, botão de cancelar e de estorno. isPaid
  // inclui FINISHED/SETTLEMENT — os dois significam pagamento efetivado.
  const backendGenericStatus = data?.status?.genericStatus
  const isCanceled =
    genericStatus === GenericStatusEnum.CANCELED ||
    backendGenericStatus === GenericStatusEnum.CANCELED
  const isRefunded =
    genericStatus === GenericStatusEnum.REFUNDED ||
    backendGenericStatus === GenericStatusEnum.REFUNDED
  const isPaid =
    genericStatus === GenericStatusEnum.PAID ||
    backendGenericStatus === GenericStatusEnum.FINISHED ||
    backendGenericStatus === GenericStatusEnum.SETTLEMENT
  // isPending fica na taxonomia rica do front: o bucket PENDING do backend é
  // grosseiro (WAITING_*/EXPIRED/REVERSED) e mostraria banner "pendente" indevido.
  const isPending =
    genericStatus === GenericStatusEnum.PENDING
  // A VTEX orquestra o desfazer pela própria plataforma; agir por fora dessincroniza os dois lados.
  const isVtexSalesChannel =
    data?.salesChannel?.status ===
      SalesChannelEnum.VTEX_PLATFORM ||
    data?.salesChannel?.status ===
      SalesChannelEnum.VTEX_SUBSCRIPTION
  const isRestrictedSalesChannel =
    data?.salesChannel?.status ===
      SalesChannelEnum.VTEX_PLATFORM ||
    data?.salesChannel?.status ===
      SalesChannelEnum.SUBSCRIPTION ||
    data?.salesChannel?.status ===
      SalesChannelEnum.VTEX_SUBSCRIPTION
  const showCancelButton =
    !isCanceled &&
    !isRefunded &&
    !isRestrictedSalesChannel &&
    !isOpenFinancePayment
  // Reverter é desfazer a cobrança já emitida — só faz sentido nos meios que têm baixa no banco.
  // Cartão sai por estorno, que é outro endpoint e outro dinheiro.
  const hasReversibleMethod = [
    PaymentMethodNameEnum.BANK_SLIP,
    PaymentMethodNameEnum.BOLEPIX,
    PaymentMethodNameEnum.PIX,
  ].some(hasPaymentMethods)
  // Pedido já revertido sai da oferta: no modo forçado a reversão seria gravada de novo. O que
  // continua elegível é SUBMITTED_FOR_REVERSED — o pedido travado é justamente o alvo do forçado.
  const isReversed =
    genericStatus === GenericStatusEnum.REVERSED ||
    backendGenericStatus === GenericStatusEnum.REVERSED
  // Pedido nascido de um ciclo reverte pela API do CICLO, com assinatura + ciclo; pedido avulso
  // reverte pelo uuid dele. A origem está no próprio dado, e é ela que decide — não o canal de
  // venda, que escondia a ação de pedidos de assinatura em vez de mandá-los para a API certa.
  const cycleOrigin =
    data?.billingCycle?.id &&
    data?.billingCycle?.subscription?.id
      ? {
          cycleId: data.billingCycle.id,
          subscriptionId: data.billingCycle.subscription.id,
        }
      : null
  const showReverseButton =
    hasReversibleMethod &&
    !isReversed &&
    !isCanceled &&
    !isRefunded &&
    !isVtexSalesChannel
  const isExpiredByDate =
    data?.expiresAt && new Date(data.expiresAt) < new Date()
  const allowedStatusToExpire = [
    StatusEnum.PENDING,
    StatusEnum.EXPIRED,
    StatusEnum.WAITING_PAYMENT_PIX,
    StatusEnum.WAITING_PAYMENT_BANK_SLIP,
    StatusEnum.WAITING_PAYMENT_BOLEPIX,
  ]
  const isAllowedStatus = currentStatus
    ? allowedStatusToExpire.includes(currentStatus)
    : false
  const { data: hasOrderInsuranceExpired = false } =
    useHasOrderInsuranceExpired(data?.id)
  const isExpiredStatus =
    currentStatus === StatusEnum.EXPIRED

  const showExpireButton =
    hasPermission([PermissionsRolesEnum.ADMIN]) &&
    isExpiredByDate &&
    isAllowedStatus &&
    (currentStatus === StatusEnum.PENDING ||
      (isExpiredStatus && !hasOrderInsuranceExpired))

  const showReprocessButton =
    hasPermission([PermissionsRolesEnum.ADMIN]) &&
    Boolean(data?.paymentId)

  const isAdmin = hasPermission([
    PermissionsRolesEnum.ADMIN,
  ])
  const showAdminSection =
    isAdmin &&
    (showExpireButton ||
      showReprocessButton ||
      showReverseButton)

  const title =
    data?.code ??
    error?.message ??
    t("orders.details.notFound")

  if (isFetching) return <OrderDetailsSkeleton />

  return (
    <>
      {showAdminSection && (
        <Card className="mb-3 border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center gap-2">
              <ShieldAlertIcon
                size={16}
                className="text-amber-800"
              />
              <h3 className="text-sm font-semibold text-amber-950">
                {t("orders.details.adminSection")}
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {showExpireButton && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExpireOrder}
                  disabled={isExpiring}
                  className="border-red-500 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 disabled:opacity-50"
                >
                  <HourglassIcon
                    size={18}
                    className="-ml-1"
                  />
                  {t("orders.expireButton")}
                </Button>
              )}
              {showReprocessButton && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReprocessPaymentEvent}
                  disabled={isReprocessing}
                  className="gap-2 border-red-500 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 disabled:opacity-50"
                >
                  <RefreshCwIcon size={18} />
                  {t("orders.reprocessPaymentEventButton")}
                </Button>
              )}
              {showReverseButton && (
                <ReversalAction
                  cycleOrigin={cycleOrigin}
                  orderId={params.orderId!}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <h2 className="text-sm font-semibold text-muted-foreground">
                {t("orders.details.title")}
              </h2>
              {hasData && (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1">
                    <h1 className="page-title">{title}</h1>
                    <ClipboardButton
                      text={data?.code || ""}
                    />
                  </div>
                  <OrdersSaleChannelBadge
                    saleChannel={
                      data?.salesChannel
                        ?.status as SalesChannelEnum
                    }
                  />
                  {data?.status && (
                    <OrdersStatusBadge
                      status={data.status}
                    />
                  )}
                </div>
              )}
            </div>
            {hasData &&
              (showCancelButton ||
                (hasTransactions &&
                  (isCanceled ||
                    isPaid ||
                    isExpiredStatus))) && (
                <div className="flex flex-wrap items-center gap-2">
                  {showCancelButton && (
                    <CancelDialog
                      orderId={params.orderId!}
                      orderCode={params.orderCode!}
                    />
                  )}
                  {hasTransactions &&
                    (isCanceled ||
                      isPaid ||
                      isExpiredStatus) && (
                      <ExportCancelLetter data={data} />
                    )}
                </div>
              )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasData && (
            <section className="rounded-md border border-destructive/40 bg-destructive/10 p-4">
              <p className="text-sm text-destructive">
                {error?.message ??
                  t("orders.details.loadError")}
              </p>
            </section>
          )}
          {hasData && (
            <>
              <section>
                {isCanceled && (
                  <div className="rounded-md border-2 border-destructive/30 bg-destructive/20 p-4">
                    <p className="text-sm">
                      {t(
                        "orders.details.statusMessages.canceled"
                      )}
                    </p>
                  </div>
                )}
                {isPaid && (
                  <div className="rounded-md border-2 border-green-500/30 bg-green-500/20 p-4">
                    <p className="text-sm">
                      {t(
                        "orders.details.statusMessages.finished"
                      )}
                    </p>
                  </div>
                )}
                {isPending && (
                  <div className="rounded-md border-2 border-yellow-500/30 bg-yellow-500/20 p-4">
                    <p className="text-sm">
                      {t(
                        "orders.details.statusMessages.pending"
                      )}
                    </p>
                  </div>
                )}
                {isRefunded && (
                  <div className="rounded-md border-2 border-blue-500/30 bg-blue-500/20 p-4">
                    <p className="text-sm">
                      {t(
                        "orders.details.statusMessages.refunded"
                      )}
                    </p>
                  </div>
                )}
              </section>
              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className="w-full"
              >
                <div className="w-full overflow-x-auto">
                  <TabsList className="mb-2 w-full justify-start sm:justify-center">
                    <TabsTrigger
                      value="details"
                      className="text-xs sm:text-sm"
                    >
                      {t("orders.details.tabs.details")}
                    </TabsTrigger>
                    {data?.isInsurance && (
                      <TabsTrigger
                        value="insurance"
                        className="text-xs sm:text-sm"
                      >
                        {t("orders.details.tabs.insurance")}
                      </TabsTrigger>
                    )}
                    {data?.isSubscription &&
                      hasSubscription && (
                        <TabsTrigger
                          value="subscription"
                          className="text-xs sm:text-sm"
                        >
                          {t(
                            "orders.details.tabs.subscription"
                          )}
                        </TabsTrigger>
                      )}
                    <TabsTrigger
                      value="transactions"
                      className="text-xs sm:text-sm"
                    >
                      {t(
                        "orders.details.tabs.transactions"
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="status"
                      className="text-xs sm:text-sm"
                    >
                      {t(
                        "orders.details.tabs.statusTracking"
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="devices"
                      className="text-xs sm:text-sm"
                    >
                      {t("orders.details.tabs.devices")}
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent
                  value="details"
                  className="space-y-6"
                >
                  <section className="grid gap-4 lg:grid-cols-2">
                    <SectionCard
                      title={t("orders.details.orderInfo")}
                      icon={<ClipboardListIcon size={16} />}
                    >
                      <div className="grid gap-4 sm:grid-cols-2">
                        <InfoItem
                          label="ID"
                          value={data?.id}
                          copyable
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.orderNumber"
                          )}
                          value={data?.code}
                          copyable
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.referenceCode"
                          )}
                          value={data?.ownId}
                          copyable
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.purchaseDate"
                          )}
                          value={createdAt}
                        />
                        {data?.paymentMethod && (
                          // O meio de pagamento vinha no payload e era lido só para detectar
                          // Open Finance: abrir o pedido não dizia se ele foi Pix ou cartão.
                          <InfoItem
                            label={t(
                              "orders.details.fields.paymentMethod"
                            )}
                            value={getPaymentMethodLabel(
                              data.paymentMethod,
                              t
                            )}
                          />
                        )}
                        <InfoItem
                          label={t(
                            "orders.details.fields.detailedStatus"
                          )}
                          value={data?.status?.description}
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.status"
                          )}
                          value={
                            data?.status?.status
                              ? t(
                                  `orders.statusTracking.orderStatus.${
                                    getStatusConfig(
                                      data.status
                                        .status as StatusTrackingEnum
                                    ).labelKey
                                  }`
                                )
                              : undefined
                          }
                        />
                        {data?.paidAt && (
                          <InfoItem
                            label={t(
                              "orders.details.fields.paidAt"
                            )}
                            value={DateHelper.format(
                              data.paidAt,
                              "dd/MM/yyyy HH:mm"
                            )}
                          />
                        )}
                        {data?.expiresAt && (
                          <InfoItem
                            label={t(
                              "orders.details.fields.expiresAt"
                            )}
                            value={DateHelper.format(
                              data.expiresAt,
                              "dd/MM/yyyy HH:mm"
                            )}
                          />
                        )}
                        {data?.canceledAt && (
                          <InfoItem
                            label={t(
                              "orders.details.fields.canceledAt"
                            )}
                            value={DateHelper.format(
                              data.canceledAt,
                              "dd/MM/yyyy HH:mm"
                            )}
                          />
                        )}
                      </div>
                    </SectionCard>

                    <SectionCard
                      title={t("orders.details.totalValue")}
                      icon={<ReceiptTextIcon size={16} />}
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-center gap-2 sm:items-baseline sm:justify-start">
                          <span className="text-3xl font-bold text-primary">
                            {data?.totalAmount
                              ? formatMoney(
                                  data.totalAmount / 100
                                )
                              : "-"}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {t(
                              "orders.details.totalAmountNote"
                            )}
                          </span>
                        </div>
                        <Separator />
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <InfoItem
                            label={t(
                              "orders.details.fields.amount"
                            )}
                            value={
                              data?.amount
                                ? formatMoney(
                                    data?.amount / 100
                                  )
                                : "-"
                            }
                          />
                          <InfoItem
                            label={t(
                              "orders.table.discount"
                            )}
                            value={
                              data?.discount
                                ? `-${formatMoney(data?.discount / 100)}`
                                : formatMoney(0)
                            }
                          />
                          <InfoItem
                            label={t(
                              "orders.details.fields.interest"
                            )}
                            value={
                              data?.interest
                                ? formatMoney(
                                    data?.interest / 100
                                  )
                                : formatMoney(0)
                            }
                          />
                        </div>
                      </div>
                    </SectionCard>

                    <SectionCard
                      title={t(
                        "orders.details.customerInfo"
                      )}
                      icon={<UserIcon size={16} />}
                      action={
                        customer?.id && (
                          <Link
                            to={`/customers/details/${customer.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              buttonVariants({
                                variant: "ghost",
                                size: "sm",
                              }),
                              "h-7 gap-1 text-xs"
                            )}
                          >
                            <ExternalLinkIcon size={12} />
                            {t("common.viewDetails")}
                          </Link>
                        )
                      }
                    >
                      <div className="grid gap-4 sm:grid-cols-2">
                        <InfoItem
                          label={t("common.name")}
                          value={customer?.fullName}
                        />
                        <InfoItem
                          label={t("common.document")}
                          value={formatDocument(
                            customer?.document
                          )}
                          copyable
                        />
                        <InfoItem
                          label={t("common.email")}
                          value={customer?.email}
                          copyable
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.contact"
                          )}
                          value={phoneNumber}
                        />
                        {customer?.address && (
                          <div className="col-span-full">
                            <Separator className="mb-3" />
                            <div className="flex items-start gap-2">
                              <MapPinIcon
                                size={14}
                                className="mt-0.5 shrink-0 text-muted-foreground"
                              />
                              <div className="text-sm">
                                <p>
                                  {customer.address.street},{" "}
                                  {
                                    customer.address
                                      .streetNumber
                                  }
                                  {customer.address
                                    .complement &&
                                    ` - ${customer.address.complement}`}
                                </p>
                                <p className="text-muted-foreground">
                                  {
                                    customer.address
                                      .neighborhood
                                  }{" "}
                                  - {customer.address.city}/
                                  {customer.address.state}
                                </p>
                                <p className="text-muted-foreground">
                                  {t("common.zipCode")}:{" "}
                                  {customer.address.zipCode}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </SectionCard>

                    <SectionCard
                      title={t(
                        "orders.details.merchantInfo"
                      )}
                      icon={<StoreIcon size={16} />}
                    >
                      <div className="grid gap-4 sm:grid-cols-2">
                        <InfoItem
                          label={t("common.name")}
                          value={
                            data?.merchant?.fantasyName
                          }
                        />
                        <InfoItem
                          label={t("common.document")}
                          value={formatDocument(
                            data?.merchant?.document
                          )}
                          copyable
                        />
                        {data?.merchant?.address && (
                          <div className="col-span-full">
                            <Separator className="mb-3" />
                            <span className="mb-2 block text-xs font-medium text-muted-foreground">
                              {t("common.address")}
                            </span>
                            <div className="space-y-1 text-sm">
                              <p className="text-muted-foreground">
                                {
                                  data.merchant.address
                                    .street
                                }
                                ,{" "}
                                {
                                  data.merchant.address
                                    .streetNumber
                                }{" "}
                                {data.merchant.address
                                  .complement &&
                                  `- ${data.merchant.address.complement}`}
                              </p>
                              <p className="text-muted-foreground">
                                {
                                  data.merchant.address
                                    .neighborhood
                                }
                                ,{" "}
                                {data.merchant.address.city}{" "}
                                -{" "}
                                {
                                  data.merchant.address
                                    .state
                                }
                              </p>
                              <p className="text-muted-foreground">
                                {t("common.zipCode")}:{" "}
                                {
                                  data.merchant.address
                                    .zipCode
                                }
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </SectionCard>
                  </section>
                  {hasProducts && (
                    <section className="space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground">
                          {t("orders.details.products")}
                        </h3>
                      </div>
                      <ProductsTable
                        items={data?.items || []}
                      />
                    </section>
                  )}
                </TabsContent>
                <TabsContent
                  value="insurance"
                  className="space-y-6"
                >
                  <section className="grid gap-4 lg:grid-cols-2">
                    <SectionCard
                      title={t(
                        "orders.details.insuranceInfo"
                      )}
                      icon={<ShieldCheckIcon size={16} />}
                    >
                      <div className="grid gap-4 sm:grid-cols-2">
                        <InfoItem
                          label={t(
                            "orders.details.fields.policy"
                          )}
                          value={
                            data?.insurance?.policyNumber
                          }
                          copyable
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.type"
                          )}
                          value={
                            data?.insurance?.type?.name
                              ? getInsuranceTypeLabel(
                                  data.insurance.type.name,
                                  t
                                )
                              : undefined
                          }
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.createdAt"
                          )}
                          value={
                            data?.insurance?.createdAt
                              ? DateHelper.format(
                                  data.insurance.createdAt,
                                  "dd/MM/yyyy HH:mm"
                                )
                              : undefined
                          }
                        />

                        {hasCars && (
                          <div className="col-span-full">
                            <Separator className="my-3" />
                            <span className="mb-2 block text-xs font-medium text-muted-foreground">
                              {t(
                                "orders.details.fields.vehicles"
                              )}
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {data?.insurance?.policyInformation?.cars?.map(
                                (car) => (
                                  <div
                                    key={
                                      car.licensePlate ||
                                      car.model
                                    }
                                    className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2"
                                  >
                                    <CarIcon
                                      size={14}
                                      className="text-muted-foreground"
                                    />
                                    <div className="text-sm">
                                      <span className="font-medium">
                                        {car.model}
                                      </span>
                                      <span className="mx-1 text-muted-foreground">
                                        •
                                      </span>
                                      <span className="text-muted-foreground">
                                        {car.year}
                                      </span>
                                      <span className="mx-1 text-muted-foreground">
                                        •
                                      </span>
                                      <span className="font-mono text-xs">
                                        {car.licensePlate}
                                      </span>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </SectionCard>

                    {broker && (
                      <SectionCard
                        title={t(
                          "orders.details.brokerInfo"
                        )}
                        icon={<BuildingIcon size={16} />}
                      >
                        <div className="grid gap-4 sm:grid-cols-2">
                          <InfoItem
                            label={t("common.name")}
                            value={broker?.name}
                          />
                          <InfoItem
                            label={t(
                              "orders.details.fields.cnpj"
                            )}
                            value={formatDocument(
                              broker?.document
                            )}
                            copyable
                          />
                          <InfoItem
                            label={t("common.email")}
                            value={broker?.email}
                            copyable
                          />
                          {broker?.phone && (
                            <InfoItem
                              label={t(
                                "orders.details.fields.contact"
                              )}
                              value={formatPhoneNumber(
                                `${broker.phone.areaCode}${broker.phone.number}`
                              )}
                            />
                          )}
                        </div>
                      </SectionCard>
                    )}
                  </section>
                </TabsContent>
                <TabsContent
                  value="subscription"
                  className="space-y-6"
                >
                  <section className="space-y-4">
                    <SectionCard
                      title={t(
                        "orders.details.subscriptionInfo"
                      )}
                      icon={<RefreshCwIcon size={16} />}
                      action={
                        data?.billingCycle?.subscription
                          ?.id && (
                          <Link
                            to={`/subscriptions/details/${data.billingCycle.subscription.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              buttonVariants({
                                variant: "ghost",
                                size: "sm",
                              }),
                              "h-7 gap-1 text-xs"
                            )}
                          >
                            <ExternalLinkIcon size={12} />
                            {t("common.viewDetails")}
                          </Link>
                        )
                      }
                    >
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <InfoItem
                          label={t(
                            "orders.details.fields.id"
                          )}
                          value={
                            data?.billingCycle?.subscription
                              ?.id
                          }
                          copyable
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.code"
                          )}
                          value={
                            data?.billingCycle?.subscription
                              ?.code
                          }
                          copyable
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.status"
                          )}
                          value={
                            data?.billingCycle?.subscription
                              ?.status
                              ? t(
                                  subscriptionStatusLabels[
                                    data.billingCycle
                                      .subscription
                                      .status as SubscriptionStatusEnum
                                  ]
                                )
                              : undefined
                          }
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.type"
                          )}
                          value={
                            data?.billingCycle?.subscription
                              ?.type
                              ? t(
                                  subscriptionTypeLabels[
                                    data.billingCycle
                                      .subscription
                                      .type as SubscriptionTypeEnum
                                  ]
                                )
                              : undefined
                          }
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.startDate"
                          )}
                          value={DateHelper.formatDate(
                            data?.billingCycle?.subscription
                              ?.startDate,
                            "dd/MM/yyyy"
                          )}
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.endDate"
                          )}
                          value={DateHelper.formatDate(
                            data?.billingCycle?.subscription
                              ?.endDate,
                            "dd/MM/yyyy"
                          )}
                        />
                        <InfoItem
                          label={t("common.value")}
                          value={
                            data?.billingCycle?.subscription
                              ?.amount
                              ? formatMoney(
                                  data.billingCycle
                                    .subscription.amount /
                                    100
                                )
                              : undefined
                          }
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.billingCycles"
                          )}
                          value={data?.billingCycle?.subscription?.billingCycles?.toString()}
                        />
                        <InfoItem
                          label={t(
                            "orders.details.fields.recurringUnit"
                          )}
                          value={
                            data?.billingCycle?.subscription
                              ?.recurringUnit
                              ? t(
                                  recurringUnitLabels[
                                    data.billingCycle
                                      .subscription
                                      .recurringUnit as RecurringUnitEnum
                                  ]
                                )
                              : undefined
                          }
                        />
                        {data?.billingCycle?.subscription
                          ?.description && (
                          <div className="col-span-full">
                            <InfoItem
                              label={t(
                                "orders.details.fields.description"
                              )}
                              value={
                                data.billingCycle
                                  .subscription.description
                              }
                            />
                          </div>
                        )}
                      </div>
                    </SectionCard>

                    {hasBillingCycle && (
                      <SectionCard
                        title={t(
                          "orders.details.billingCycleInfo"
                        )}
                        icon={<CalendarIcon size={16} />}
                      >
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          <InfoItem
                            label={t(
                              "orders.details.fields.id"
                            )}
                            value={data?.billingCycle?.id}
                            copyable
                          />
                          <InfoItem
                            label={t(
                              "orders.details.fields.cycle"
                            )}
                            value={data?.billingCycle?.billingCycle?.toString()}
                          />
                          <InfoItem
                            label={t("common.value")}
                            value={
                              data?.billingCycle?.amount
                                ? formatMoney(
                                    data.billingCycle
                                      .amount / 100
                                  )
                                : undefined
                            }
                          />
                          <InfoItem
                            label={t(
                              "orders.details.fields.dueDate"
                            )}
                            value={DateHelper.formatDate(
                              data?.billingCycle?.dueDate,
                              "dd/MM/yyyy"
                            )}
                          />
                          <InfoItem
                            label={t(
                              "orders.details.fields.processingDate"
                            )}
                            value={DateHelper.formatDate(
                              data?.billingCycle
                                ?.processingDate,
                              "dd/MM/yyyy"
                            )}
                          />
                          <InfoItem
                            label={t(
                              "orders.details.fields.status"
                            )}
                            value={getBillingCycleStatusLabel(
                              data?.billingCycle?.status
                            )}
                          />
                        </div>
                      </SectionCard>
                    )}
                  </section>
                </TabsContent>
                <TabsContent
                  value="transactions"
                  className="space-y-4"
                >
                  {hasTransactions ? (
                    <>
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-muted-foreground">
                          {t(
                            "orders.details.transactionsDetails"
                          )}
                        </h3>
                        {data?.receiptUrls?.pdf && (
                          <Button asChild variant="default">
                            <Link
                              to={data.receiptUrls.pdf}
                              target="_blank"
                            >
                              {t("orders.details.receipt")}
                            </Link>
                          </Button>
                        )}
                      </div>
                      <TransactionsTable
                        transactions={
                          data?.transactions || []
                        }
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center rounded-md border border-dashed p-8">
                      <p className="text-sm text-muted-foreground">
                        {t("orders.details.noTransactions")}
                      </p>
                    </div>
                  )}
                </TabsContent>
                <TabsContent
                  value="status"
                  className="space-y-6"
                >
                  {data?.events &&
                  data.events.length > 0 ? (
                    <>
                      {/* Summary header */}
                      <div className="flex flex-col gap-4 rounded-lg border bg-gradient-to-r from-muted/50 to-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                            <ClockIcon
                              size={20}
                              className="text-primary"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">
                              {t(
                                "orders.details.tabs.statusTracking"
                              )}
                            </p>
                            <p className="text-lg font-semibold">
                              {data.events.length}{" "}
                              {data.events.length === 1
                                ? t("common.event")
                                : t("common.events")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarIcon size={14} />
                          <span>
                            {t("orders.details.lastUpdate")}
                            :{" "}
                            {DateHelper.formatDateTime(
                              data.events[0]?.createdAt,
                              "dd/MM/yyyy HH:mm"
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div className="relative">
                        {/* Vertical gradient line */}
                        <div className="absolute bottom-0 left-5 top-0 w-0.5 bg-gradient-to-b from-primary/50 via-border to-transparent" />

                        <div className="space-y-0">
                          {data.events.map(
                            (event, index) => {
                              const isFirst = index === 0
                              const statusConfig =
                                getStatusConfig(
                                  event.status as StatusTrackingEnum
                                )

                              return (
                                <div
                                  key={index}
                                  className="relative flex gap-4 pb-1"
                                >
                                  {/* Timeline node */}
                                  <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center">
                                    <div
                                      className={cn(
                                        "flex h-8 w-8 items-center justify-center rounded-full border-2 border-background shadow-md transition-transform hover:scale-110",
                                        statusConfig.bgColor
                                      )}
                                    >
                                      {isFirst ? (
                                        <CheckCircle2Icon
                                          size={16}
                                          className="text-white"
                                        />
                                      ) : (
                                        <div className="h-2 w-2 rounded-full bg-white" />
                                      )}
                                    </div>
                                  </div>

                                  {/* Content card */}
                                  <div
                                    className={cn(
                                      "group mb-4 flex-1 rounded-xl border bg-card p-4 shadow-sm transition-all hover:shadow-md",
                                      isFirst &&
                                        "border-primary/30 bg-gradient-to-r from-primary/5 to-transparent"
                                    )}
                                  >
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                      <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                          <span
                                            className={cn(
                                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white",
                                              statusConfig.bgColor
                                            )}
                                          >
                                            {t(
                                              `orders.statusTracking.orderStatus.${statusConfig.labelKey}`
                                            )}
                                          </span>
                                          {isFirst && (
                                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                              {t(
                                                "common.current"
                                              )}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                          {
                                            event.description
                                          }
                                        </p>
                                        {getAdditionalDetail(
                                          event.description,
                                          event.statusDetails
                                        ) && (
                                          <p className="rounded-md border-l-2 border-amber-400 bg-amber-50 px-2 py-1 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                                            {getAdditionalDetail(
                                              event.description,
                                              event.statusDetails
                                            )}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                        <CalendarIcon
                                          size={12}
                                        />
                                        <time>
                                          {DateHelper.formatDateTime(
                                            event.createdAt,
                                            "dd/MM/yyyy HH:mm:ss"
                                          )}
                                        </time>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/20 p-12">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                        <ClockIcon
                          size={24}
                          className="text-muted-foreground"
                        />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t(
                          "orders.details.noStatusTracking"
                        )}
                      </p>
                    </div>
                  )}
                </TabsContent>
                <TabsContent
                  value="devices"
                  className="space-y-6"
                >
                  {/* Só monta ao abrir a aba: dado sensível (IP, geolocalização,
                      fingerprint) não é buscado sem o operador pedir. */}
                  {activeTab === "devices" &&
                    params.orderId && (
                      <DevicesTab
                        orderId={params.orderId}
                      />
                    )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </CardContent>
      </Card>
    </>
  )
}
